/**
 * Global legacy-nav-icon resolver (BO UI Standard v3, 2026-09-18).
 * ---------------------------------------------------------------------------
 * Makes EVERY navigation item — sidebar group header, sidebar leaf, icon rail,
 * and the on-page <FormTitle> — show the REAL legacy ALPACA LIMS (XAF) icon,
 * matched by the nav item's stable `id`. One mechanism, applied app-wide.
 *
 * HOW TO GIVE ANY NAV ITEM ITS LEGACY ICON  (works for current AND any nav
 * item added hereafter — no code change, no per-item wiring):
 *   1. Extract the real legacy PNG (custom icons live in the legacy
 *      BTLIMS.Module.Web/Images; DevExpress action/BO icons are embedded in
 *      DevExpress.ExpressApp.Images.v20.1.dll — e.g. Action_ChooseSkin, BO_*).
 *   2. Drop it into  src/assets/nav-legacy/  named EXACTLY  "<navId>.png"
 *      (the nav id from navData.ts / nav-registry.json — e.g. grp-reporting.png,
 *      sampreg.png, at-ai.png).
 * That's it. `import.meta.glob` below picks it up at build and every place that
 * renders that nav id uses it automatically. A nav id with no matching PNG keeps
 * its normal Tabler/emoji icon (graceful fallback), so nothing else changes.
 */

// Eagerly import every PNG in the legacy-icon folder as its served URL.
const files = import.meta.glob('../assets/nav-legacy/*.png', {
  eager: true,
  query: '?url',
  import: 'default',
}) as Record<string, string>;

/** navId -> legacy icon URL, keyed by the PNG's basename. */
const ICON_BY_ID: Record<string, string> = {};
for (const [path, url] of Object.entries(files)) {
  const file = path.split('/').pop();
  if (!file) continue;
  ICON_BY_ID[file.replace(/\.png$/i, '')] = url;
}

/** The legacy icon URL for a nav id, or undefined if none is provided. */
export function legacyNavIcon(id?: string): string | undefined {
  return id ? ICON_BY_ID[id] : undefined;
}

/**
 * Resolve a nav icon globally: the legacy PNG for `id` when one exists in
 * src/assets/nav-legacy/, otherwise the item's own Tabler/emoji `fallback`.
 * Call this at every NavIcon render site that knows the nav id, and inside
 * navMeta() so <FormTitle> matches the sidebar.
 */
export function resolveNavIcon(id: string | undefined, fallback: string): string {
  return (id ? ICON_BY_ID[id] : undefined) ?? fallback;
}
